import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Flex,
  Spinner,
  Text,
  Badge,
} from '@chakra-ui/react';
import axios from 'axios';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n) => String(n).padStart(2, '0');

function formatNice(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return `${pad2(d)} ${MONTH_LABELS[m - 1]} ${y}`;
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

const EditStrikeRate = ({ isOpen, onClose, selectedRetailer, selectedSupplier, onDataSaved }) => {
  const [weekEndingDates, setWeekEndingDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [newDay, setNewDay] = useState('');
  const [newMonth, setNewMonth] = useState('');
  const [newYear, setNewYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 1; y >= currentYear - 6; y -= 1) arr.push(y);
    return arr;
  }, [currentYear]);

  const dayOptions = useMemo(() => {
    const max = newMonth && newYear ? daysInMonth(Number(newYear), Number(newMonth)) : 31;
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [newMonth, newYear]);

  const newDate =
    newYear && newMonth && newDay ? `${newYear}-${pad2(newMonth)}-${pad2(newDay)}` : '';

  useEffect(() => {
    if (isOpen) {
      fetchWeekEndingDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedRetailer]);

  useEffect(() => {
    if (!selectedDate) return;
    const [y, m, d] = selectedDate.split('-');
    if (y && m && d) {
      setNewYear(y);
      setNewMonth(String(Number(m)));
      setNewDay(String(Number(d)));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (newMonth && newYear && newDay) {
      const max = daysInMonth(Number(newYear), Number(newMonth));
      if (Number(newDay) > max) setNewDay(String(max));
    }
  }, [newMonth, newYear, newDay]);

  const fetchWeekEndingDates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/rep_strikeRate/week-ending-date`,
        { retailer: selectedRetailer }
      );
      if (response.data.data && Array.isArray(response.data.data)) {
        setWeekEndingDates(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedDate(response.data.data[0].week_ending_date || '');
        }
      } else {
        setWeekEndingDates([]);
      }
    } catch (error) {
      console.error('Error fetching week ending dates:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch week ending dates',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWeekEndingDate = async () => {
    if (!selectedDate || !newDate) {
      toast({
        title: 'Missing date',
        description: 'Please select both a current and a new date',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (selectedDate === newDate) {
      toast({
        title: 'No change',
        description: 'The new date is the same as the current date',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/upd-week-ending-date`, {
        retailer: selectedRetailer,
        old_date: selectedDate,
        new_date: newDate,
      });

      toast({
        title: 'Success',
        description: `Renamed ${formatNice(selectedDate)} -> ${formatNice(newDate)}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchWeekEndingDates();
      if (onDataSaved) onDataSaved();
    } catch (error) {
      console.error('Error updating week ending date:', error);
      toast({
        title: 'Error',
        description: 'Failed to update week ending date',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const canSubmit = Boolean(selectedDate) && Boolean(newDate) && selectedDate !== newDate;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Strike Rate Data</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                <Tab>Dates</Tab>
                <Tab>Other</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <VStack spacing={5} align="stretch">
                    <FormControl>
                      <FormLabel fontWeight="semibold">Current Week-Ending Date</FormLabel>
                      <Select
                        size="lg"
                        bg="white"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      >
                        {weekEndingDates.map((dateObj, index) => (
                          <option key={index} value={dateObj.week_ending_date}>
                            {formatNice(dateObj.week_ending_date)}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold">New Date</FormLabel>
                      <HStack spacing={2} align="stretch">
                        <Select
                          size="lg"
                          flex="1"
                          bg="white"
                          placeholder="Day"
                          value={newDay}
                          onChange={(e) => setNewDay(e.target.value)}
                        >
                          {dayOptions.map((d) => (
                            <option key={d} value={d}>
                              {pad2(d)}
                            </option>
                          ))}
                        </Select>
                        <Select
                          size="lg"
                          flex="1.2"
                          bg="white"
                          placeholder="Month"
                          value={newMonth}
                          onChange={(e) => setNewMonth(e.target.value)}
                        >
                          {MONTH_LABELS.map((m, i) => (
                            <option key={m} value={i + 1}>
                              {m}
                            </option>
                          ))}
                        </Select>
                        <Select
                          size="lg"
                          flex="1"
                          bg="white"
                          placeholder="Year"
                          value={newYear}
                          onChange={(e) => setNewYear(e.target.value)}
                        >
                          {yearOptions.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </Select>
                      </HStack>
                    </FormControl>

                    {selectedDate && newDate && (
                      <Box
                        bg={selectedDate === newDate ? 'gray.50' : 'blue.50'}
                        borderWidth="1px"
                        borderColor={selectedDate === newDate ? 'gray.200' : 'blue.200'}
                        p={3}
                        borderRadius="md"
                      >
                        <Text fontSize="sm" color="gray.700">
                          {selectedDate === newDate ? (
                            <>No change &mdash; new date matches current date.</>
                          ) : (
                            <>
                              Will rename{' '}
                              <Badge colorScheme="red" fontSize="xs">
                                {formatNice(selectedDate)}
                              </Badge>{' '}
                              &rarr;{' '}
                              <Badge colorScheme="green" fontSize="xs">
                                {formatNice(newDate)}
                              </Badge>
                            </>
                          )}
                        </Text>
                      </Box>
                    )}

                    <Button
                      colorScheme="blue"
                      size="lg"
                      onClick={handleUpdateWeekEndingDate}
                      isLoading={isSaving}
                      isDisabled={!canSubmit}
                    >
                      Update Date
                    </Button>
                  </VStack>
                </TabPanel>

                {/* Other Tab - Left intentionally blank */}
                <TabPanel>
                  <Box p={4} borderWidth="1px" borderRadius="md">
                    <Flex justify="center" align="center" minH="100px">
                      <Box as="span" color="gray.500">
                        Additional configuration options will be available here in future updates.
                      </Box>
                    </Flex>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditStrikeRate;
